import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { alpha, useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { TUploadedFile } from '@onesource/schemas';
import { EImageVariant } from '@onesource/schemas';
import { memo, useCallback, useMemo, useState } from 'react';
import { MdCheckCircle, MdCrop, MdDeleteOutline, MdZoomIn } from 'react-icons/md';
import { PiImageBrokenDuotone } from 'react-icons/pi';

import { ActionIconButton } from '../button';
import { CopyUrlMenu } from './copy-url-menu';
import { MediaThumb } from './media-thumb';

export type TMediaThumbnailProps = {
  file: TUploadedFile;
  isSelected: boolean;
  onSelect: (file: TUploadedFile) => void;
  onPreview: (file: TUploadedFile) => void;
  /** When set, disables selection if the file was not uploaded with this variant */
  selectVariant?: EImageVariant;
  /** Called when the user clicks Resize on a disabled (too-small) thumbnail */
  onResize?: (file: TUploadedFile) => void;
  /** Called when user confirms deletion. Omit to hide the delete button. */
  onDelete?: (file: TUploadedFile) => void;
};

export const MediaThumbnail = memo((props: TMediaThumbnailProps) => {
  const { file, isSelected, onSelect, onPreview, selectVariant, onResize, onDelete } = props;
  const theme = useTheme();
  const primaryMain = theme.palette.primary.main;

  const [imgErrored, setImgErrored] = useState(false);
  const handleImgError = useCallback(() => setImgErrored(true), []);

  const isDisabled =
    imgErrored || (!!selectVariant && selectVariant !== EImageVariant.original && !file.cdnUrls?.[selectVariant]);

  const canResize = !imgErrored && isDisabled && !!file.publicUrl && !!onResize;

  const overlayBtnSx = useMemo(
    () => ({
      p: '6px',
      width: 40,
      height: 40,
      borderRadius: '6px',
      bgcolor: 'rgba(255,255,255,0.95)',
      color: 'text.primary',
      boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.25)}`,
      transition: 'transform 0.1s',
      '&:hover': { bgcolor: 'white', transform: 'scale(1.1)' },
    }),
    [theme.palette.common.black],
  );

  return (
    <Tooltip
      title={imgErrored ? 'Image is broken' : isDisabled ? 'Image not available in the required size' : ''}
      placement="top"
      arrow
    >
      <Box
        sx={{
          position: 'relative',
          borderRadius: 2,
          overflow: 'hidden',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          border: `2.5px solid ${isSelected ? primaryMain : 'transparent'}`,
          boxShadow: isSelected
            ? `0 0 0 1px ${primaryMain}, 0 6px 18px ${alpha(primaryMain, 0.3)}`
            : `0 1px 4px ${alpha(theme.palette.common.black, 0.08)}`,
          transition: 'border-color 0.1s, box-shadow 0.1s, opacity 0.1s',
          bgcolor: theme.palette.grey[100],
          ...(!isDisabled || canResize || (imgErrored && !!onDelete)
            ? {
                '&:hover': {
                  border: `2.5px solid ${isSelected ? primaryMain : alpha(primaryMain, 0.45)}`,
                  boxShadow: isSelected
                    ? `0 0 0 1px ${primaryMain}, 0 6px 18px ${alpha(primaryMain, 0.3)}`
                    : `0 6px 18px ${alpha(theme.palette.common.black, 0.18)}`,
                  '& .thumb-overlay': {
                    opacity: 1,
                    pointerEvents: 'auto',
                  },
                },
              }
            : {}),
        }}
        onClick={() => !isDisabled && onSelect(file)}
      >
        {/* Image */}
        <Box
          sx={{
            width: '100%',
            aspectRatio: '1/1',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ width: '100%', height: '100%' }}>
            {file.publicUrl ? (
              <MediaThumb
                src={file.publicUrl}
                alt={file.filename}
                size={32}
                fallbackColor={theme.palette.action.disabled}
                onError={handleImgError}
              />
            ) : (
              <PiImageBrokenDuotone size={40} color={theme.palette.action.disabled} />
            )}
          </Box>
        </Box>

        {/* Disabled overlay — semi-transparent white wash */}
        {isDisabled && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: alpha(theme.palette.common.white, 0.55),
              pointerEvents: 'none',
            }}
          />
        )}

        {/* Hover overlay — CSS-driven, no React state */}
        {(file.publicUrl && !imgErrored) || (imgErrored && !!onDelete) ? (
          <Box
            className="thumb-overlay"
            sx={{
              position: 'absolute',
              inset: 0,
              background: `linear-gradient(180deg, ${alpha(theme.palette.common.black, 0.35)} 0%, transparent 50%)`,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-end',
              gap: 1,
              p: 0.75,
              opacity: 0,
              pointerEvents: 'none',
              transition: 'opacity 0.1s ease',
            }}
          >
            {canResize && (
              <Button
                variant="contained"
                size="small"
                startIcon={<MdCrop size={16} />}
                onClick={e => {
                  e.stopPropagation();
                  onResize(file);
                }}
                sx={{ pointerEvents: 'auto', mr: 'auto' }}
              >
                Resize
              </Button>
            )}
            {!canResize && (
              <>
                {!imgErrored && (
                  <ActionIconButton
                    icon={<MdZoomIn size={20} />}
                    tooltip="Preview"
                    sx={overlayBtnSx}
                    onClick={e => {
                      e.stopPropagation();
                      onPreview(file);
                    }}
                  />
                )}
                {!imgErrored && file.cdnUrls && (
                  <Box onClick={e => e.stopPropagation()} sx={{ lineHeight: 0 }}>
                    <CopyUrlMenu
                      cdnUrls={file.cdnUrls}
                      variantSizes={{}}
                      standalone
                      iconSize={20}
                      buttonSx={overlayBtnSx}
                    />
                  </Box>
                )}
              </>
            )}
            {onDelete && (
              <ActionIconButton
                icon={<MdDeleteOutline size={20} />}
                tooltip="Delete"
                sx={{
                  ...overlayBtnSx,
                  color: 'error.main',
                  '&:hover': { bgcolor: 'white', transform: 'scale(1.1)', color: 'error.dark' },
                }}
                onClick={e => {
                  e.stopPropagation();
                  onDelete(file);
                }}
              />
            )}
          </Box>
        ) : null}

        {/* Selected badge */}
        {isSelected && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(primaryMain, 0.25),
            }}
          >
            <Box
              sx={{
                color: primaryMain,
                bgcolor: 'background.paper',
                borderRadius: '50%',
                lineHeight: 0,
                boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.25)}`,
              }}
            >
              <MdCheckCircle size={36} />
            </Box>
          </Box>
        )}

        {/* Filename strip */}
        <Tooltip title={file.filename} placement="top" arrow>
          <Box
            sx={{
              px: 1,
              py: 0.75,
              bgcolor: 'background.paper',
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="caption" noWrap sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem' }}>
              {file.filename}
            </Typography>
          </Box>
        </Tooltip>
      </Box>
    </Tooltip>
  );
});
